import Switch from '@carvana/showroom-forms/Switch';
import classNames from 'classnames';
import useBudgetTradeIn from '@/FiltersMenu/Budget/hooks/useBudgetTradeIn';
import TradeToolTip from '@/FiltersMenu/Budget/TradeToolTip';
import { SELL_MY_CAR_URL } from '~/constants';
import { useTradeInfo } from '~/contexts/FinanceAndTradeContext';
import { useHasAppliedTrade } from '~/contexts/SearchContext';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import Tag from './Tag';

export default function HasTradeCard() {
  const {
    acquisitionId,
    make,
    model,
    mobileImage,
    year,
    trim,
    status,
    totalTradeInEquity = 0,
    totalTradeInValue = 0
  } = useTradeInfo();
  const isExpired = status === 'expired';
  const isActive = !isExpired && totalTradeInValue;
  const isPending = !isExpired && totalTradeInValue === 0;
  const loanBalance = totalTradeInValue - totalTradeInEquity;
  const isPositiveEquity = totalTradeInEquity > 0;
  const tradeUrl = acquisitionId
    ? `/sell-my-car/hub/${acquisitionId}`
    : SELL_MY_CAR_URL;
  const tradeIsApplied = useHasAppliedTrade();

  const toggleTrade = useBudgetTradeIn();

  return (
    <div
      className={
        'border border-grey-2 border-solid grid grid-rows-[repeat(4,auto)] p-24 rounded-[6px]'
      }
      data-qa="trade-in-card"
    >
      <div className="flex flex-row items-center justify-between mb-16">
        <h3 className="inline mb-0 text-blue-6 tl-header-s">Your Trade-in</h3>
        {isActive ? (
          <Switch
            checked={tradeIsApplied}
            onChange={() => {
              toggleTrade();
            }}
            data-qa={tradeIsApplied ? 'isOn' : 'isOff'}
          />
        ) : (
          <Tag
            text={`Offer ${isPending ? 'Pending' : 'Expired'}`}
            bgColor={classNames(
              '!h-[24px] !text-blue-6 float-right justify-center rounded-xl tl-label-xs uppercase w-[97px]',
              {
                'bg-blue-1': isPending,
                'bg-yellow-2': isExpired
              }
            )}
            data-qa="no-active-trade-badge"
          />
        )}
      </div>
      <div
        className={classNames(
          'flex flex-row gab-8 items-center',
          isExpired ? 'mb-16' : 'mb-8'
        )}
      >
        {mobileImage ? (
          <img
            className="bg-grey-1 mr-16 rounded-lg w-80"
            src={mobileImage}
            alt="vehicle-image"
          />
        ) : null}
        <p className="text-[14px] text-blue-6">
          {year} {make} {model} {trim}
        </p>
      </div>
      {isActive ? (
        <div className="flex items-center mb-16">
          <p className="mr-5 text-grey-5">
            {isPositiveEquity ? 'Trade-in value:' : 'Balance due:'}
          </p>
          <div className="flex items-center ml-4 tl-body-l">
            <p
              className={classNames('font-bold text-blue-6', {
                'text-red-2': !isPositiveEquity
              })}
            >
              ${formatAndDropDecimal(Math.abs(totalTradeInEquity))}
            </p>
            {loanBalance > 0 && (
              <div className="flex ml-4 self-center">
                <TradeToolTip
                  equity={totalTradeInEquity}
                  loanBalance={loanBalance}
                  tradeValue={totalTradeInValue}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
      <p
        className={classNames('mb-16 text-grey-5 tl-body-m', {
          hidden: !isPending
        })}
      >
        We’re in the process of reviewing your trade-in details.
      </p>
      <a
        className="text-left tl-link-m"
        data-analytics-id="add a trade-in"
        data-native-payload={JSON.stringify({
          action: 'view-trade-offer'
        })}
        href={tradeUrl}
      >
        {isActive
          ? 'View offer details'
          : isExpired
          ? 'Refresh your offer'
          : 'Try another vehicle'}
      </a>
    </div>
  );
}
